import React from 'react';
import { Play, Pause } from 'lucide-react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useNavigate } from 'react-router-dom';

export const TrackCard = ({ track }) => {
  const { currentTrack, isPlaying, playTrack } = useAudioPlayer();
  const navigate = useNavigate();
  
  const isCurrentTrack = currentTrack?.id === track.id;
  const isTrackPlaying = isCurrentTrack && isPlaying;

  const handlePlay = (e) => {
    e.stopPropagation();
    playTrack(track);
  };

  const handleCardClick = () => {
    navigate(`/tracks/${track.id}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div
      onClick={handleCardClick}
      className="track-card border border-white/10 rounded-lg cursor-pointer group overflow-hidden"
      data-testid={`track-card-${track.id}`}
    >
      {/* Artwork */}
      <div className="relative aspect-square overflow-hidden">
        <img
          src={track.artwork_url}
          alt={track.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Play Button Overlay */}
        <div className="play-overlay absolute inset-0 bg-black/50 flex items-center justify-center">
          <button
            onClick={handlePlay}
            className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center hover:bg-emerald-400 transition-colors transform hover:scale-105"
            data-testid={`track-play-${track.id}`}
          >
            {isTrackPlaying ? (
              <Pause className="w-6 h-6 text-slate-900" />
            ) : (
              <Play className="w-6 h-6 text-slate-900 ml-1" />
            )}
          </button>
        </div>

        {/* Tags */}
        <div className="absolute top-3 left-3 flex gap-2">
          {track.genre && (
            <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-xs text-white rounded-full">
              {track.genre}
            </span>
          )}
          {track.bpm && (
            <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-xs text-slate-400 rounded-full">
              {track.bpm} BPM
            </span>
          )}
        </div>

        {/* Featured Badge */}
        {track.is_featured && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 bg-emerald-500 text-xs font-semibold text-slate-900 rounded-full">
              Featured
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-white truncate group-hover:text-emerald-400 transition-colors" data-testid={`track-title-${track.id}`}>
          {track.title}
        </h3>
        <p className="text-sm text-slate-400 truncate mt-1">
          {track.artist_name}
        </p>

        {/* Price & Stats */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">From</p>
            <p className="text-lg font-semibold text-emerald-400" data-testid={`track-price-${track.id}`}>
              {formatPrice(track.price_sync)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">{track.total_sales || 0} sales</p>
          </div>
        </div>
      </div>
    </div>
  );
};
